import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { dataSourceLinkSelectionSchema } from "../data-source-link-selection";

/**
 * Observe DataSource Link Trigger
 *
 * Entry point for workflows that react to row-level changes on a DataSource
 * Link's underlying table. Backed by Change Data Capture (Postgres logical
 * replication → Debezium → the `cdc-events` queue → CdcDispatchWorker → a durable
 * staging table → an in-process flusher).
 *
 * The trigger is intentionally a **notification + watermark + key manifest**, not
 * a row-data carrier: full row contents would be far too much to push through
 * Temporal for high-churn tables, and one-workflow-run-per-row does not scale.
 * Instead the flusher coalesces streamed changes per table and starts ONE run per
 * batch carrying only `{pk, op, lsn}` per change; downstream steps fetch the
 * rows they need by primary key from the ingest DB.
 *
 * The DataSource Link(s) to observe are chosen via the shared selection config
 * (`dataSourceLinkStrategy`): 'static' watches one specific link, while
 * 'matching_schema' watches every link conforming to a chosen SchemaDefinition —
 * subscribing to CDC for all of their tables and dispatching a run for whichever
 * table changed (each run still carries the single link that changed).
 *
 * Publication lifecycle: publishing a workflow that uses this trigger adds each
 * observed link's table to the shared CDC publication (`ALTER PUBLICATION ADD
 * TABLE`); the last workflow to stop watching a table removes it again
 * (reference-counted). So the configured selection decides which tables we capture.
 *
 * Two run phases (see `phase`):
 *   - "initial": one run emitted at publish time. `changes` is empty;
 *     `watermarkLsn` is the WAL position captured right AFTER streaming was
 *     switched on. Dependent steps use it to backfill (SELECT the table as-of the
 *     watermark) and establish their baseline. Changes at/after the watermark
 *     arrive as "change" runs, so backfill and stream meet with no gap and no
 *     double-count (reconcile by LSN).
 *   - "change": a batch of streamed changes — the `changes` manifest plus the
 *     batch's max `watermarkLsn`. The steady state.
 *
 * Ordering is NOT guaranteed across runs — dependent steps must use each change's
 * `lsn` for last-writer-wins, never arrival order. See
 * docs/workflows/observe-datasource-link-cdc-plan.md.
 */

const dataSourceLinkSchema = z.object({
  id: z.string().uuid().describe("DataSource Link id being observed"),
  dataSourceId: z.string().uuid().describe("Parent DataSource id"),
  schemaName: z.string().describe("Observed table schema"),
  tableName: z.string().describe("Observed table name"),
});

/** Step type of the Observe DataSource Link (CDC) trigger. */
export const OBSERVE_DATA_SOURCE_LINK_STEP_TYPE = "observe_data_source_link";

export const observeDataSourceLinkStep = defineStepConfig({
  type: OBSERVE_DATA_SOURCE_LINK_STEP_TYPE,
  name: "Observe DataSource Link",
  description:
    "Triggers the workflow on batched row-level changes to a DataSource Link's table (Change Data Capture). Each run carries a batch of changed rows (full row image + op + lsn) + a watermark LSN. A special initial run carries the backfill watermark.",
  category: "Trigger",

  experimental: {
    reason:
      "Observe DataSource Link is under active development and not yet validated for production workflows.",
  },

  // Entry point — no inputs. The change manifest / initial watermark arrive via
  // the orchestrator's initial inputs (set by the CDC flusher / the publish hook).
  inputSchema: z.never(),

  // Item-shaped, batched outputs. `dataSourceLink` + `phase` + `metadataId` are
  // reserved; the per-field `array<T>` sockets (one per schema column) are
  // dynamic, resolved from the link's SchemaDefinition (see
  // generateDynamicOutputSockets). Each per-field array and `metadataId` are
  // index-aligned (one entry per row in the window/batch); the engine's lockstep
  // fanout (driven by the downstream step's `batch` config) zips them into
  // per-item tuples, broadcasting the single `dataSourceLink`.
  outputSchema: z.object({
    dataSourceLink: dataSourceLinkSchema.describe(
      "The DataSource Link this run observed",
    ),
    // Scalar rather than a field on `dataSourceLink`, because a socket
    // expression cannot reach INTO a json socket — the language is `output`,
    // `collect`, `string_join`, `fallback` and `substring`, with no field
    // access. A lookup that has to be told which source it is reading needs
    // the title as its own wireable value, and a `<source>_<file>` table
    // family carries no such column of its own.
    dataSourceTitle: z
      .string()
      .nullable()
      .describe(
        "Title of the observed link's DataSource, broadcast across the batch.",
      ),
    phase: z
      .enum(["initial", "change"])
      .describe(
        "'initial' (full backfill, read from the table) or 'change' (a CDC batch).",
      ),
    changeLsn: z
      .array(z.string().nullable())
      .optional()
      .describe("Sequence number per row for ordered CDC sinks."),
    metadataId: z
      .array(z.string())
      .describe(
        "Surrogate `id` of each row in the batch, index-aligned with the per-field column arrays.",
      ),
  }),

  configSchema: dataSourceLinkSelectionSchema.extend({
    operationsToWatch: z
      .array(z.enum(["insert", "update", "delete"]))
      .optional()
      .describe(
        "Which DML operations should trigger a run. Defaults to all three.",
      ),
    // Batching (run size, change-dispatch wait window, and backfill page
    // concurrency) is configured once on the downstream batched step's `batch`
    // config, not here — see `WorkflowStepBatchConfigSchema`.
  }),

  emits: [] as const,
});

export type ObserveDataSourceLinkConfig = z.infer<
  typeof observeDataSourceLinkStep.configSchema
>;
